import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { useAuth } from "../context/AuthContext";
import Logo from "./common/Logo";
import AgentNavButton from "./agent/AgentNavButton";
import AgentAuthModal from "./agent/AgentAuthModal";
import { useAgentSession } from "../utils/agentAuth";
import "../utils/agentIdentityBridge"; // stamps partner identity on all API calls + re-verifies session
import "../styles/Navbar.css"; // Import the new styles

const Navbar = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, openLoginModal, handleLogout: logoutFromContext } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [agentModalOpen, setAgentModalOpen] = useState(false);
    const agentSession = useAgentSession();

    useEffect(() => {
        if (location.pathname !== "/") {
            setIsScrolled(true);
            return;
        }

        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        // Initial check
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [location.pathname]);

    const handleLogout = async () => {
        try {
            await logoutFromContext();

            // Redirect to Home ONLY if on a private/account-related page
            const privatePaths = ["/booking-history", "/booking-detail", "/book-flight", "/booking-flight"];
            const isPrivatePath = privatePaths.some(path => location.pathname.startsWith(path));

            if (isPrivatePath) {
                navigate("/");
            }

            // Reload to clear all states and stay on the current URL (if public) or Home (if private)
            window.location.reload();
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const isChatPage = location.pathname === "/chat";
    const isHomePage = location.pathname === "/";
    const isPartnerPage = location.pathname.startsWith("/partner");

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    // Helper to handle smooth scroll for home page sections
    const handleNavClick = (e, target) => {
        if (location.pathname === "/") {
            e.preventDefault();
            const element = document.querySelector(target);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
            closeMobileMenu();
        } else {
            // If not on home page, let the default link behavior (href="/#target") work
            // or navigate to home then scroll (handled by browser usually if we use /#target)
            closeMobileMenu();
        }
    };

    return (
        <nav className={`top-nav ${isScrolled ? 'is-scrolled' : 'is-transparent'}`}>
            <Logo />

            <div className="nav-actions" style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                {isHomePage && (
                    <div className="nav-links hidden lg:flex">
                        <a
                            href="/#features"
                            onClick={(e) => handleNavClick(e, "#features")}
                        >
                            Features
                        </a>

                        <a
                            href="/#faqs"
                            onClick={(e) => handleNavClick(e, "#faqs")}
                        >
                            FAQs
                        </a>

                        <Link to="/contact">
                            Contact
                        </Link>

                        {/* AI Route Search */}
                        <button
                            className="ai-agent-btn"
                            onClick={() => navigate("/chat")}
                        >
                            <img src="/Star1.svg" alt="AI" /> Plan Your Trip
                        </button>

                        {/* Curated trips (replaces Search Routes; route search lives in the hero form) */}
                        <button
                            className="download-btn"
                            onClick={() => navigate("/trips")}
                        >
                            Explore trips →
                        </button>

                        <AgentNavButton
                            onOpenLogin={() => setAgentModalOpen(true)}
                        />

                        {userProfile.code === 3 && !agentSession && (
                            <button
                                className="signin-nav-btn"
                                onClick={() => openLoginModal()}
                            >
                                Sign In
                            </button>
                        )}
                    </div>
                )}

                {children}

                {/* Other pages: only signed-in partners get a shortcut back to their dashboard */}
                {!isHomePage && !isPartnerPage && agentSession && (
                    <>
                        <div className="hidden sm:block">
                            <AgentNavButton />
                        </div>

                        <div className="sm:hidden">
                            <AgentNavButton variant="icon" />
                        </div>
                    </>
                )}

                {isHomePage ? (
                    // On home page: show avatar-only dropdown if logged in, nothing if not
                    userProfile.code === 2 ? (
                        <div
                            className="user-profile-wrapper"
                            style={{ position: "relative" }}
                        >
                            <Menu as="div" className="user-dropdown">
                                <Menu.Button
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "4px",
                                        borderRadius: "50%"
                                    }}
                                    className="transition-colors"
                                >
                                    {userProfile.photoURL &&
                                    userProfile.photoURL !== '/profileDefault.png' ? (
                                        <img
                                            src={userProfile.photoURL}
                                            alt="Profile"
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                objectFit: "cover"
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.querySelector(
                                                    '.avatar-fallback-home'
                                                ).style.display = 'flex';
                                            }}
                                        />
                                    ) : null}

                                    <div
                                        className="avatar-fallback-home"
                                        style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#FF5B15",
                                            color: "white",
                                            display:
                                                (userProfile.photoURL &&
                                                userProfile.photoURL !== '/profileDefault.png')
                                                    ? 'none'
                                                    : 'flex',
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        {userProfile.email ? (
                                            userProfile.displayName?.charAt(0) || 'U'
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        )}
                                    </div>
                                </Menu.Button>

                                <Menu.Items
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        marginTop: "4px",
                                        width: "180px",
                                        backgroundColor: "white",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                        padding: "4px 0",
                                        zIndex: 1001
                                    }}
                                >
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/booking-history"
                                                style={{
                                                    display: "block",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    textDecoration: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent"
                                                }}
                                            >
                                                My Bookings
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={handleLogout}
                                                style={{
                                                    width: "100%",
                                                    textAlign: "left",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#EF4444",
                                                    border: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                Logout
                                            </button>
                                        )}
                                    </Menu.Item>
                                </Menu.Items>
                            </Menu>
                        </div>
                    ) : null
                ) : (
                    // On all other pages: existing behavior (profile + name, or sign-in button)
                    userProfile.code === 2 ? (
                        <div
                            className="user-profile-wrapper"
                            style={{ position: "relative" }}
                        >
                            <Menu as="div" className="user-dropdown">
                                <Menu.Button
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "4px 8px",
                                        borderRadius: "8px"
                                    }}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    <span
                                        className="user-name"
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#374151",
                                            textTransform: "capitalize"
                                        }}
                                    >
                                        {userProfile.displayName}
                                    </span>

                                    {userProfile.photoURL &&
                                    userProfile.photoURL !== '/profileDefault.png' ? (
                                        <img
                                            src={userProfile.photoURL}
                                            alt="Profile"
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                objectFit: "cover"
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.querySelector(
                                                    '.avatar-fallback'
                                                ).style.display = 'flex';
                                            }}
                                        />
                                    ) : null}

                                    <div
                                        className="avatar-fallback"
                                        style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            backgroundColor: "#FF5B15",
                                            color: "white",
                                            display:
                                                (userProfile.photoURL &&
                                                userProfile.photoURL !== '/profileDefault.png')
                                                    ? 'none'
                                                    : 'flex',
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            textTransform: "uppercase"
                                        }}
                                    >
                                        {userProfile.email ? (
                                            userProfile.displayName?.charAt(0) || 'U'
                                        ) : (
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20 21v-2a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        )}
                                    </div>
                                </Menu.Button>

                                <Menu.Items
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        marginTop: "4px",
                                        width: "180px",
                                        backgroundColor: "white",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                        padding: "4px 0",
                                        zIndex: 1001
                                    }}
                                >
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/booking-history"
                                                style={{
                                                    display: "block",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    textDecoration: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent"
                                                }}
                                            >
                                                My Bookings
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={handleLogout}
                                                style={{
                                                    width: "100%",
                                                    textAlign: "left",
                                                    padding: "8px 16px",
                                                    fontSize: "14px",
                                                    color: "#EF4444",
                                                    border: "none",
                                                    backgroundColor: active
                                                        ? "#F9FAFB"
                                                        : "transparent",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                Logout
                                            </button>
                                        )}
                                    </Menu.Item>
                                </Menu.Items>
                            </Menu>
                        </div>
                    ) : userProfile.code === 3 && !agentSession ? (
                        <button
                            className="signin-nav-btn"
                            onClick={() => openLoginModal()}
                        >
                            Sign In
                        </button>
                    ) : null
                )}

                {/* Mobile Menu Button + Dropdown */}
                {isHomePage && (
                    <div style={{ position: "relative" }}>
                        <button
                            className="mobile-menu-btn lg:hidden"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            <span style={{ fontSize: '30px' }}>
                                {isMobileMenuOpen ? '✕' : '⋮'}
                            </span>
                        </button>

                        {isMobileMenuOpen && (
                            <div className="mobile-dropdown lg:hidden">

                                <a
                                    href="/#features"
                                    onClick={(e) => handleNavClick(e, "#features")}
                                >
                                    Features
                                </a>

                                <a
                                    href="/#faqs"
                                    onClick={(e) => handleNavClick(e, "#faqs")}
                                >
                                    FAQs
                                </a>

                                <Link
                                    to="/contact"
                                    onClick={closeMobileMenu}
                                >
                                    Contact
                                </Link>

                                {/* AI Route Search */}
                                <button
                                    className="mobile-nav-link-btn"
                                    onClick={() => {
                                        closeMobileMenu();
                                        navigate("/chat");
                                    }}
                                >
                                    <img
                                        src="/Star1.svg"
                                        alt="AI"
                                        style={{
                                            width: '16px',
                                            height: '16px'
                                        }}
                                    />{" "}
                                    Plan Your Trip
                                </button>

                                {/* Curated trips (replaces Search Routes) */}
                                <button
                                    className="mobile-nav-link-btn"
                                    onClick={() => {
                                        closeMobileMenu();
                                        navigate("/trips");
                                    }}
                                >
                                    Explore trips →
                                </button>

                                <AgentNavButton
                                    variant="menu"
                                    onNavigate={closeMobileMenu}
                                    onOpenLogin={() => setAgentModalOpen(true)}
                                />

                                {userProfile.code === 3 && !agentSession && (
                                    <button
                                        className="mobile-nav-link-btn"
                                        onClick={() => {
                                            closeMobileMenu();
                                            openLoginModal();
                                        }}
                                        style={{
                                            color: "#FF5B15",
                                            fontWeight: "600"
                                        }}
                                    >
                                        Sign In
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Owned here so it survives the mobile dropdown closing */}
            <AgentAuthModal
                open={agentModalOpen}
                onClose={() => setAgentModalOpen(false)}
            />
        </nav>
    );
};

export default Navbar;
